import { createContext, JSX, ReactNode, useContext, useMemo, useRef } from 'react';

/**
 * The edit screen the assistant is allowed to fill in, if any.
 *
 * `setValue` is react-hook-form's, taken from the form itself rather than rebuilt here, so an
 * accepted proposal goes through exactly the path a keystroke does - the field is marked dirty,
 * validation runs, and **nothing is saved**. The assistant stays read-only; the user saves through
 * the normal HQ path, so permissions, validation and the Customer Activity Monitor all still
 * apply.
 */
export interface ChatFormTarget {
    resource: string;
    recordId: string | number | null;
    /**
     * The inputs currently on screen, discovered from the form rather than listed per screen.
     *
     * HQ-13. This was a snapshot taken when the screen registered, and the effect that
     * registers does not depend on the field list - so an input revealed later by a
     * `FormDataConsumer` was invisible to the assistant until the screen remounted, and
     * `NokEditForm` has two. A function for the same reason `values` is one: read it when a
     * message is sent, not when the screen opened.
     */
    fields: () => string[];
    /** What those inputs currently hold, so the assistant can see what it would be changing. */
    values: () => Record<string, unknown>;
    setValue: (field: string, value: unknown) => void;
}

export interface ChatFormBridge {
    /** Publish the screen. Returns the function that withdraws it again. */
    register: (target: ChatFormTarget) => () => void;
    /** The screen on top right now, or null. */
    current: () => ChatFormTarget | null;
}

export const ChatFormBridgeContext = createContext<ChatFormBridge | null>(null);

/**
 * Lets the assistant see the form on the screen behind it.
 *
 * The panel is rendered by the app bar and the form by the page, so they are siblings and the
 * panel cannot reach the form's `FormProvider`. This sits above both - `AdminLayout` renders
 * `<Layout appBar={NokNokAppBar}>{children}</Layout>` - and a screen registers itself from inside
 * its own form.
 *
 * **Held in a ref, not in state.** Registering a screen must not re-render the application, and
 * nothing renders from this: the chat reads it when a message is sent, and a proposal card reads
 * it when the user accepts. A screen nobody registered is invisible to the assistant, which is the
 * default and the safe one.
 *
 * **A stack, not a slot.** HQ-12: with one slot a modal replaced the page form beneath it, and
 * on close its unregister found itself still published and cleared the slot - leaving the
 * assistant blind to a screen that was still open, until the admin navigated away and back. So
 * nine modal and panel forms were left unbridged. A stack answers both that and the navigation
 * order that made the single slot awkward: React mounts the next edit screen before unmounting
 * the last, so the new one is pushed on top and the old is removed from underneath it.
 */
export const ChatFormBridgeProvider: (props: { children: ReactNode }) => JSX.Element =
    ({ children }): JSX.Element => {
        const stack = useRef<ChatFormTarget[]>([]);

        const bridge: ChatFormBridge = useMemo((): ChatFormBridge => ({
            register: (screen: ChatFormTarget): (() => void) => {
                stack.current = [...stack.current, screen];

                return (): void => {
                    // Remove this screen wherever it sits, rather than clearing the top. A page
                    // form unmounting while its modal is still open must not take the modal's
                    // registration with it, and vice versa.
                    stack.current = stack.current.filter(
                        (published: ChatFormTarget): boolean => published !== screen
                    );
                };
            },
            // The last to register is the one in front of the admin: a modal over a page form,
            // or the edit screen just navigated to.
            current: (): ChatFormTarget | null => stack.current[stack.current.length - 1] ?? null,
        }), []);

        return (
            <ChatFormBridgeContext.Provider value={ bridge }>
                { children }
            </ChatFormBridgeContext.Provider>
        );
    };

export function useChatFormBridge(): ChatFormBridge | null {
    return useContext(ChatFormBridgeContext);
}

/** What the assistant is told about the screen, sent with each message. */
export interface ChatScreen {
    resource: string;
    recordId: string | number | null;
    fields: string[];
    values: Record<string, unknown>;
}

/** How much of one field's value travels with a message. A long note is not worth the tokens. */
const MAX_VALUE_LENGTH: number = 200;

/**
 * Describe the open screen for the assistant.
 *
 * Values as well as names, because "does this address look right to you?" needs them and because
 * without them the assistant will propose a value a field already holds. Everything here is on
 * the screen the signed-in admin is already looking at, so this shows the model nothing they
 * cannot see - but it is trimmed: only the fields the form published, only scalars, and only the
 * first couple of hundred characters of each.
 */
export function describeScreen(bridge: ChatFormBridge | null): ChatScreen | null {
    const target: ChatFormTarget | null = bridge?.current() ?? null;
    if (target === null) {
        return null;
    }
    // Read once: the list is built from react-hook-form's mounted register on each call, and
    // the two loops below must agree about what is on screen.
    const fields: string[] = target.fields();
    if (fields.length === 0) {
        return null;
    }

    const values: Record<string, unknown> = {};
    const held: Record<string, unknown> = target.values();

    fields.forEach((field: string): void => {
        const value: unknown = held[field];
        if (value === null || value === undefined || typeof value === 'object') {
            // A relation or an embedded array is not something the assistant can fill in, so it
            // is named in `fields` and left out of the values.
            return;
        }
        const text: string = String(value);
        values[field] = text.length > MAX_VALUE_LENGTH ? text.slice(0, MAX_VALUE_LENGTH) + '...' : text;
    });

    return {
        resource: target.resource,
        recordId: target.recordId,
        fields: fields,
        values: values,
    };
}
